import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button/Button";
import { ButtonSize } from "@/components/Button/types";

import { CreateCompanyEnrich } from "@/pages/Private/pages/CompanyEnrich/schema/companyEnrich";

import { TrackFilterFields } from "./TrackFilterFields";
import {
	EMPTY_TRACK_FILTERS,
	TrackFilterValues,
	hasAnyTrackFilter,
	toPreviewRequest,
} from "./trackFilters";
import { useTrackFilterLookups } from "./useTrackFilterLookups";

/**
 * The Track step's "find different people" pane — a rail beside the table
 * (mockup `.tk-filterpane`): four primary filters, a collapsed advanced group,
 * and a Search that re-runs the suggestions with these overrides.
 */
export const TrackFilterPane: FunctionComponent<{
	onSearch: (request: CreateCompanyEnrich) => void;
	isSearching: boolean;
	/**
	 * The criteria the suggestions were actually built with — the rung the relaxation
	 * ladder reached, not the ICP seed. The step waits for them before rendering, so
	 * the pane mounts already carrying them. Mount-time state: from here on the values
	 * are the user's, and nothing outside pushes into them.
	 */
	initialValues?: TrackFilterValues | null;
}> = ({ onSearch, isSearching, initialValues }) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`onboarding.track.${key}`);

	const lookups = useTrackFilterLookups();
	const [values, setValues] = useState<TrackFilterValues>(initialValues ?? EMPTY_TRACK_FILTERS);
	const patch = (next: Partial<TrackFilterValues>) => setValues((prev) => ({ ...prev, ...next }));

	return (
		<div className="flex max-h-full w-[270px] shrink-0 flex-col self-stretch overflow-hidden rounded-xl border border-gray-200 bg-gray-50">
			<div className="flex shrink-0 items-center border-b border-gray-200 px-4 py-3">
				<span className="text-xs font-bold">{ts("filterPeopleTitle")}</span>
			</div>

			<div className="min-h-0 flex-1 overflow-y-auto py-3">
				<div className="flex flex-col gap-3 px-4">
					<TrackFilterFields lookups={lookups} values={values} onChange={patch} />
				</div>
			</div>

			<div className="shrink-0 border-t border-gray-200 p-3">
				<Button
					className="w-full"
					disabled={!hasAnyTrackFilter(values) || isSearching}
					isLoading={isSearching}
					size={ButtonSize.ML}
					title={ts("filterSearch")}
					type="button"
					onClick={() => onSearch(toPreviewRequest(values))}
				/>
			</div>
		</div>
	);
};
